import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Rss, Search, Tag as TagIcon } from "lucide-react";
import { StartHereStrip } from "@/components/StartHere";
import {
  fetchBlogPosts,
  fetchBlogCategories,
  fetchBlogTags,
  fetchCategoryLanding,
  blogHeroUrl,
  categoryHeroUrl,
  API,
} from "@/lib/api";

const PAGE_TITLE = "The Umbra Journal — Shadow Work, Rituals, and Inner Craft";
const PAGE_DESC =
  "Essays on shadow work, voice-first journaling, and the practical mechanics of self-inquiry. New drops every fortnight.";
const SEARCH_CHIPS = ["anger", "inner child", "forgiveness", "patterns", "journaling", "relationships", "shame"];
const chipTopic = (c) => c.replace(/\s+/g, "-");
// Field Notes shows every post on one page (API max is 100).
const PAGE_SIZE = 100;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function buildTitle({ category, tag, page }) {
  const parts = [];
  if (category) parts.push(`Category: ${category}`);
  if (tag) parts.push(`#${tag}`);
  if (page && page > 1) parts.push(`Page ${page}`);
  const suffix = parts.length ? ` — ${parts.join(" · ")}` : "";
  return `The Umbra Journal${suffix}`;
}

/**
 * Blog index — handles four route shapes:
 *   /blog
 *   /blog/page/:page
 *   /blog/category/:category  (also /blog/category/:category/page/:page)
 *   /blog/tag/:tag            (also /blog/tag/:tag/page/:page)
 */
export default function Blog() {
  const { category: pathCategory, tag: pathTag } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Prefer path params (canonical). Fall back to query params for back-compat.
  const category = pathCategory || searchParams.get("category") || "";
  const tag = pathTag || searchParams.get("tag") || "";
  // Legacy /page/:n URLs resolve to the single full list.
  const page = 1;

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [categoryMeta, setCategoryMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q
    ? posts.filter((p) =>
        [p.title, p.excerpt, ...(p.tags || [])].some((f) => (f || "").toLowerCase().replace(/-/g, " ").includes(q.replace(/-/g, " ")))
      )
    : posts;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setCategoryMeta(null);
      try {
        const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
        if (category) params.category = category;
        if (tag) params.tag = tag;
        const promises = [
          fetchBlogPosts(params),
          fetchBlogCategories(),
          fetchBlogTags(),
        ];
        if (category && page === 1) {
          promises.push(fetchCategoryLanding(category).catch(() => null));
        }
        const [postsRes, catRes, tagRes, landingRes] = await Promise.all(promises);
        if (cancelled) return;
        setPosts(postsRes.posts || []);
        setTotal(postsRes.total || 0);
        setCategories(catRes || []);
        setTags(tagRes || []);
        if (landingRes && landingRes.category) {
          setCategoryMeta(landingRes.category);
        }
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category, tag, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const canonical = useMemo(() => {
    try {
      const base = window.location.origin;
      let path = "/blog";
      if (category) path += `/category/${category}`;
      else if (tag) path += `/tag/${tag}`;
      if (page > 1) path += `/page/${page}`;
      return `${base}${path}`;
    } catch {
      return "/blog";
    }
  }, [category, tag, page]);

  const title = useMemo(() => {
    if (categoryMeta?.seo_title) return categoryMeta.seo_title;
    return buildTitle({ category, tag, page });
  }, [categoryMeta, category, tag, page]);
  const desc = categoryMeta?.seo_description
    ? categoryMeta.seo_description
    : tag
    ? `Umbra Journal essays tagged #${tag}.`
    : category
    ? `Umbra Journal essays in the ${category} category.`
    : PAGE_DESC;

  const goPage = (n) => {
    const parts = [];
    if (category) parts.push(`category/${category}`);
    else if (tag) parts.push(`tag/${tag}`);
    if (n > 1) parts.push(`page/${n}`);
    navigate(`/blog${parts.length ? "/" + parts.join("/") : ""}`);
  };

  return (
    <div className="min-h-screen bg-obsidian-900" data-testid="blog-page">
      <Helmet>
        <html lang="en" />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Umbra Journal"
          href={`${API}/blog/feed.xml`}
        />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <a
        href="#blog-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-terracotta focus:text-obsidian-900 focus:px-3 focus:py-2 focus:font-sans focus:text-xs focus:uppercase focus:tracking-[0.24em]"
      >
        Skip to content
      </a>

      <header className="border-b border-obsidian-600 px-6 md:px-12 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/"
            data-testid="blog-home-link"
            className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
          >
            ← Umbra
          </Link>
          <a
            href={`${API}/blog/feed.xml`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-rss-link"
            aria-label="RSS feed"
            className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
          >
            <Rss className="h-3 w-3" aria-hidden="true" />
            RSS
          </a>
        </div>
      </header>

      <main id="blog-main">
        {categoryMeta && page === 1 ? (
          <section
            className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-10"
            data-testid="blog-category-landing"
          >
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-3">
              Category
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 leading-tight mb-6"
              data-testid="blog-title"
            >
              {categoryMeta.title}
            </h1>
            {categoryMeta.hero_url && (
              <div className="mb-8 border border-obsidian-600">
                <img
                  src={categoryHeroUrl(categoryMeta.slug)}
                  alt={categoryMeta.hero_alt || categoryMeta.title}
                  width="1200"
                  height="500"
                  className="w-full aspect-[24/10] object-cover"
                />
              </div>
            )}
            <p className="font-serif text-lg sm:text-xl text-bone-300 max-w-3xl leading-[1.75]">
              {categoryMeta.intro}
            </p>
          </section>
        ) : (
          <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-10">
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-3">
              {tag ? `Tagged #${tag}` : category ? `Category · ${category}` : "Field notes"}
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 leading-tight mb-4"
              data-testid="blog-title"
            >
              {tag ? `#${tag}` : category ? category : "The Journal"}
            </h1>
            <p className="font-serif italic text-lg text-bone-400 max-w-2xl leading-relaxed">
              {desc}
            </p>
          </section>
        )}

        <section className="max-w-6xl mx-auto px-6 md:px-12 pb-6">
          <label className="relative block max-w-xl">
            <span className="sr-only">Search Field Notes</span>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-bone-500" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by word or topic — anger, inner child, forgiveness…"
              data-testid="blog-search-input"
              className="w-full bg-obsidian-800/60 border border-obsidian-600 focus:border-terracotta outline-none pl-11 pr-4 h-12 font-sans text-sm text-bone-100 placeholder:text-bone-500 transition-colors"
            />
          </label>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="blog-search-chips">
            {SEARCH_CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setQuery(query.trim().toLowerCase() === c ? "" : c)}
                data-testid={`blog-search-chip-${c.replace(/\s+/g, "-")}`}
                aria-pressed={q === c}
                className={`font-sans text-[0.7rem] lowercase border px-3 min-h-[32px] transition-colors ${
                  q === c ? "border-terracotta text-terracotta" : "border-obsidian-600 text-bone-400 hover:border-bone-400 hover:text-bone-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <p className="mt-3 font-sans text-[0.7rem] uppercase tracking-[0.2em] text-bone-500" data-testid="blog-topic-links">
            Topic guides:{" "}
            {SEARCH_CHIPS.map((c, i) => (
              <React.Fragment key={c}>
                {i > 0 && " · "}
                <Link to={`/blog/topic/${chipTopic(c)}`} data-testid={`blog-topic-link-${chipTopic(c)}`} className="hover:text-terracotta">{c}</Link>
              </React.Fragment>
            ))}
          </p>
          {q && (
            <p className="mt-3 font-sans text-xs uppercase tracking-[0.2em] text-bone-500" aria-live="polite" data-testid="blog-search-count">
              {shown.length} {shown.length === 1 ? "post matches" : "posts match"} “{query.trim()}”
              {SEARCH_CHIPS.includes(q) && (
                <Link to={`/blog/topic/${chipTopic(q)}`} data-testid="blog-search-topic-link" className="ml-3 text-terracotta hover:text-bone-100">
                  Read the {q} guide →
                </Link>
              )}
            </p>
          )}
        </section>

        {(categories.length > 0 || tags.length > 0) && (
          <section
            className="max-w-6xl mx-auto px-6 md:px-12 pb-8"
            aria-label="Browse by category and tag"
          >
            {categories.length > 0 && (
              <div className="mb-4">
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-2">
                  Categories
                </p>
                <div className="flex flex-wrap gap-2" data-testid="blog-categories">
                  <Link
                    to="/blog"
                    className={`font-sans text-[0.7rem] uppercase tracking-wider border px-3 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
                      !category && !tag
                        ? "border-terracotta text-terracotta"
                        : "border-obsidian-600 text-bone-400 hover:border-bone-400"
                    }`}
                    data-testid="blog-filter-all"
                  >
                    All
                  </Link>
                  {categories.map((c) => (
                    <Link
                      key={c.name}
                      to={`/blog/category/${c.name}`}
                      data-testid={`blog-filter-cat-${c.name}`}
                      className={`font-sans text-[0.7rem] uppercase tracking-wider border px-3 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
                        category === c.name
                          ? "border-terracotta text-terracotta"
                          : "border-obsidian-600 text-bone-400 hover:border-bone-400"
                      }`}
                    >
                      {c.name} · {c.count}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {tags.length > 0 && (
              <div>
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-2">
                  Tags
                </p>
                <div className="flex flex-wrap gap-2" data-testid="blog-tags">
                  {tags.slice(0, 20).map((t) => (
                    <Link
                      key={t.name}
                      to={`/blog/tag/${t.name}`}
                      data-testid={`blog-filter-tag-${t.name}`}
                      className={`font-sans text-[0.65rem] lowercase border px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
                        tag === t.name
                          ? "border-terracotta text-terracotta"
                          : "border-obsidian-600 text-bone-500 hover:border-bone-400 hover:text-bone-300"
                      }`}
                    >
                      #{t.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}


        {!category && !tag && !q && <StartHereStrip />}

        <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24">
          {loading ? (
            <p className="font-sans text-bone-500 text-sm" aria-live="polite">
              Loading…
            </p>
          ) : shown.length === 0 ? (
            <div className="border border-obsidian-600 bg-obsidian-800/30 p-10 text-center" data-testid={q ? "blog-search-empty" : "blog-empty"}>
              <p className="font-serif italic text-bone-400">
                {q ? "Nothing matches that yet. Try a broader word." : "No posts yet — the first entries are being drafted."}
              </p>
            </div>
          ) : (
            <>
              <ul
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 list-none p-0"
                data-testid="blog-post-list"
              >
                {shown.map((p) => (
                  <li key={p.id} className="list-none">
                    <article
                      data-testid={`blog-card-${p.slug}`}
                      className="group flex flex-col border border-obsidian-600 bg-obsidian-800/30 hover:border-terracotta/70 focus-within:border-terracotta transition-colors h-full"
                    >
                      <Link to={`/blog/${p.slug}`} className="block focus:outline-none">
                        <div className="relative aspect-[16/9] overflow-hidden bg-obsidian-800">
                          {p.hero_url ? (
                            <img
                              src={blogHeroUrl(p.id)}
                              alt={p.hero_alt || p.title}
                              loading="lazy"
                              width="1200"
                              height="675"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center text-bone-600 font-serif italic"
                              aria-hidden="true"
                            >
                              Umbra
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          {p.category && (
                            <Link
                              to={`/blog/category/${p.category}`}
                              className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-terracotta hover:underline"
                            >
                              {p.category}
                            </Link>
                          )}
                          <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500">
                            · {formatDate(p.published_at)}
                          </span>
                          {p.reading_time && (
                            <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500">
                              · {p.reading_time} min
                            </span>
                          )}
                        </div>
                        <h2 className="font-serif text-2xl text-bone-100 leading-snug mb-2">
                          <Link
                            to={`/blog/${p.slug}`}
                            className="hover:text-terracotta focus:outline-none focus-visible:text-terracotta focus-visible:underline transition-colors"
                          >
                            {p.title}
                          </Link>
                        </h2>
                        <p className="font-serif italic text-bone-400 text-[0.95rem] leading-relaxed mb-4 line-clamp-3">
                          {p.excerpt || p.meta_description}
                        </p>
                        {p.tags && p.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {p.tags.slice(0, 3).map((t) => (
                              <Link
                                key={t}
                                to={`/blog/tag/${t}`}
                                className="font-sans text-[0.55rem] lowercase text-bone-500 border border-obsidian-600 px-2 py-0.5 inline-flex items-center gap-1 hover:border-terracotta hover:text-terracotta focus:outline-none focus-visible:ring-1 focus-visible:ring-terracotta"
                              >
                                <TagIcon className="h-2.5 w-2.5" aria-hidden="true" />
                                {t}
                              </Link>
                            ))}
                          </div>
                        )}
                        <Link
                          to={`/blog/${p.slug}`}
                          data-testid={`blog-read-more-${p.slug}`}
                          aria-label={`Read ${p.title}`}
                          className="mt-auto inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-300 hover:text-terracotta focus:outline-none focus-visible:text-terracotta focus-visible:underline"
                        >
                          Read <ArrowRight className="h-3 w-3" aria-hidden="true" />
                        </Link>
                      </div>
                    </article>
                  </li>
                ))}
              </ul>

              {totalPages > 1 && (
                <nav
                  className="mt-16 flex items-center justify-between border-t border-obsidian-600 pt-8"
                  aria-label="Pagination"
                  data-testid="blog-pagination"
                >
                  <button
                    onClick={() => goPage(page - 1)}
                    disabled={page <= 1}
                    data-testid="blog-page-prev"
                    className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 px-2 py-1"
                  >
                    ← Newer
                  </button>
                  <span className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-500">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => goPage(page + 1)}
                    disabled={page >= totalPages}
                    data-testid="blog-page-next"
                    className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 px-2 py-1"
                  >
                    Older →
                  </button>
                </nav>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
